export const supportedDisplayCurrencies = ['USD', 'CAD'] as const;

export type DisplayCurrency = typeof supportedDisplayCurrencies[number];

export const defaultDisplayCurrency: DisplayCurrency = 'USD';

const displayCurrencyLocales: Record<DisplayCurrency, string> = {
  USD: 'en-US',
  CAD: 'en-CA'
};

const defaultCurrencyCode = defaultDisplayCurrency;

export const resolveCurrencyCode = (value?: string | null): string => {
  const normalizedValue = value?.trim().toUpperCase();

  return normalizedValue || defaultCurrencyCode;
};

export const getCurrencyLocale = (currencyCode?: string | null): string | undefined => {
  const resolvedCurrencyCode = resolveCurrencyCode(currencyCode);

  if (resolvedCurrencyCode in displayCurrencyLocales) {
    return displayCurrencyLocales[resolvedCurrencyCode as DisplayCurrency];
  }

  return undefined;
};

export const normalizeDisplayCurrency = (value?: string | null): DisplayCurrency => {
  const normalizedValue = value?.trim().toUpperCase();

  if (normalizedValue && supportedDisplayCurrencies.includes(normalizedValue as DisplayCurrency)) {
    return normalizedValue as DisplayCurrency;
  }

  return defaultDisplayCurrency;
};

export const formatCurrency = (
  value: number,
  displayCurrency: string | undefined,
  options: Intl.NumberFormatOptions = {}
): string => {
  const currencyCode = resolveCurrencyCode(displayCurrency);

  return new Intl.NumberFormat(getCurrencyLocale(currencyCode), {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options
  }).format(value);
};

export const formatNumberForCurrencyLocale = (
  value: number,
  localeCurrencyCode?: string,
  options: Intl.NumberFormatOptions = {}
): string => {
  return new Intl.NumberFormat(getCurrencyLocale(localeCurrencyCode), options).format(value);
};

export const formatInstrumentPrice = (
  value: number,
  instrumentCurrency?: string | null,
  options: Intl.NumberFormatOptions = {}
): string => {
  const normalizedInstrumentCurrency = instrumentCurrency?.trim().toUpperCase() || defaultDisplayCurrency;
  const instrumentPriceOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  };

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: normalizedInstrumentCurrency,
      ...instrumentPriceOptions
    }).format(value);
  } catch {
    return formatCurrency(value, defaultDisplayCurrency, instrumentPriceOptions);
  }
};

export const getCurrencyAxisTitle = (displayCurrency?: string): string => {
  return `Value (${normalizeDisplayCurrency(displayCurrency)})`;
};

export const getDegradationWarningMessage = (degradationReasons: string[] = []): string => {
  if (degradationReasons.length === 0) {
    return 'Some valuation inputs were unavailable, so the total may be materially incomplete.';
  }

  return `Some valuation inputs were unavailable, so the total may be materially incomplete. ${degradationReasons.join(' ')}`;
};